import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { Field, useFormikContext } from "formik";
import FileUploadForm from "../../Appointment/CompanyOfficialsTab/FileUploadForm";
import { getChangedDocumentsModuleData } from "../../Appointment/CompanyOfficialsTab/FileUploadForm/documentData";
import { useDispatch, useSelector } from "react-redux";
import OnlyOfficeDocumentEditor from "components/lib/Shared/OnlyOfficeDocumentEditor";
import { getUploadChangeDocumentsAsync } from "state/slices/company";
import { getFullName } from "utils";
import { Constants } from "config/constants";
import { format } from "date-fns";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";

const DocumentsUpload = React.memo(
  ({ field, form, resolvedFields = [], label, ...props }) => {
    const { setFieldValue } = useFormikContext();
    const dispatch = useDispatch();
    const [documentNames, setDocumentNames] = useState({});
    const { company, official } = useSelector(
      (state) => state.companyDashboardSlice
    );
    const currentUser = useSelector((state) => state.userSlice);
    const isStatutoryDeclaration = field.name === "statutoryDeclaration";
    const isLetterOfResignation = field.name === "letterOfResignation";
    const isNoticeOfRemoval = field.name === "noticeOfRemoval";

    const documentFetchedRef = useRef({});
    const initializedRef = useRef({});
    const mountedRef = useRef(true);
    const { row } = props;

    const updateDocumentName = useCallback((fieldName, fileName) => {
      setDocumentNames((prev) => ({
        ...prev,
        [fieldName]: fileName,
      }));
    }, []);

    const { isInUpdateCompanyDetailsContext } = useContext(
      UpdateCompanyDetailsContext
    );

    useEffect(() => {
      if (isInUpdateCompanyDetailsContext) {
        setFieldValue(`${field.name}.modeType`, "pre-signed-documents");
      }
    }, [isInUpdateCompanyDetailsContext]);

    useEffect(() => {
      if (!form.values.names && currentUser?.names) {
        setFieldValue("names", currentUser.names);
      }
      if (
        !form.values.postalAddress?.postalAddress &&
        currentUser?.postalAddress?.postalAddress
      ) {
        setFieldValue("postalAddress", {
          postalAddress: currentUser.postalAddress.postalAddress,
          postalCode: currentUser.postalAddress.postalCode || "",
          serviceAddress: currentUser.postalAddress.serviceAddress || "N/A",
        });
      }
      if (
        !form.values.residentialAddressDetails &&
        currentUser?.residentialAddressDetails
      ) {
        setFieldValue(
          "residentialAddressDetails",
          currentUser.residentialAddressDetails
        );
      }
    }, [currentUser, form.values, setFieldValue]);

    const componentProps = useMemo(
      () => ({
        form,
        documentName: documentNames[field.name] || "",
        currentUser,
        company,
        sectionName: field.name,
        title: label || getDocumentTitle(field.name),
        showRadioOptions:
          isStatutoryDeclaration || isLetterOfResignation || isNoticeOfRemoval,
        isStatutoryDeclaration,
        isLetterOfResignation,
        isNoticeOfRemoval,
        row: props.row,
        documentFetchedRef,
        updateDocumentName,
        isInUpdateCompanyDetailsContext,
      }),
      [
        form,
        documentNames,
        currentUser,
        company,
        field.name,
        label,
        isStatutoryDeclaration,
        isLetterOfResignation,
        isNoticeOfRemoval,
        props.row,
        documentFetchedRef,
        updateDocumentName,
        isInUpdateCompanyDetailsContext,
      ]
    );

    const handleSetValue = useCallback(
      (e) => {
        const selectedValue = e.target.value;
        setFieldValue(`${field.name}.modeType`, selectedValue);
        if (selectedValue === "pre-signed-documents") {
          setFieldValue(`${field.name}.subModeType`, "self-resolution-type");
          setFieldValue(
            `get${
              field.name.charAt(0).toUpperCase() + field.name.slice(1)
            }Name`,
            ""
          );
        } else if (selectedValue === "not-signed-documents") {
          setFieldValue(`${field.name}.subModeType`, "");
          documentFetchedRef.current[field.name] = false;
          setFieldValue(
            `get${
              field.name.charAt(0).toUpperCase() + field.name.slice(1)
            }Name`,
            ""
          );
        }
      },
      [field.name, setFieldValue]
    );

    useEffect(() => {
      if (
        !isStatutoryDeclaration &&
        !isLetterOfResignation &&
        !isNoticeOfRemoval &&
        !initializedRef.current[field.name]
      ) {
        const currentValue = form.values[field.name];
        if (!currentValue?.modeType) {
          setFieldValue(`${field.name}`, {
            modeType: "pre-signed-documents",
            subModeType: "self-resolution-type",
          });
        }
        initializedRef.current[field.name] = true;
      }
    }, [
      field.name,
      setFieldValue,
      isStatutoryDeclaration,
      isLetterOfResignation,
      isNoticeOfRemoval,
      form.values,
    ]);

    useEffect(() => {
      return () => {
        mountedRef.current = false;
      };
    }, []);

    const getDocumentData = useCallback(
      async (documentType) => {
        if (documentFetchedRef.current[field.name] || !mountedRef.current)
          return null;
        if (form.values[field.name]?.modeType !== "not-signed-documents")
          return null;

        const officialId = official?._id;

        if (!officialId) {
          console.error("No official ID available");
          return null;
        }

        try {
          const response = await dispatch(
            getUploadChangeDocumentsAsync({
              companyId: company._id,
              data: {
                officialId,
                official,
                designation: official?.designation,
                fileType: documentType,
                dateOfCessation: form.values.cessationDate,
                name: getFullName(official.names),
                postalAddress: official.postalAddress,
                cityOfResidence: official.residentialAddressDetails,
                isNonResident: official.isNonResident,
              },
            })
          ).unwrap();

          if (response?.fileName && mountedRef.current) {
            documentFetchedRef.current[field.name] = true;
            return response.fileName;
          }
          return null;
        } catch (error) {
          console.error("Failed to fetch document:", error);
          return null;
        }
      },
      [dispatch, company._id, official, form.values, field.name, mountedRef]
    );

    useEffect(() => {
      const fieldNameKey = `get${
        field.name.charAt(0).toUpperCase() + field.name.slice(1)
      }Name`;
      const subModeType = form.values[field.name]?.subModeType;
      const modeType = form.values[field.name]?.modeType;
      const existingDocumentName = form.values[fieldNameKey];

      if (
        modeType === "not-signed-documents" &&
        subModeType === "platform-resolution-type" &&
        !existingDocumentName &&
        !documentFetchedRef.current[field.name] &&
        mountedRef.current
      ) {
        let documentType;
        switch (field.name) {
          case "letterOfResignation":
            documentType =
              Constants.Template_FILE_TYPES.DIRECTOR_RESIGNATION_LETTER;
            break;
          case "shareholdersSpecialNotice":
            documentType =
              Constants.Template_FILE_TYPES.SHAREHOLDERS_SPECIAL_NOTICE;
            break;
          case "deathCertificate":
            documentType = Constants.Template_FILE_TYPES.DEATH_CERTIFICATE;
            break;
          case "noticeOfRemoval":
            documentType =
              Constants.Template_FILE_TYPES.NOTICE_OF_REMOVAL_BY_APPOINTOR;
            break;
          case "insolvencyOrder":
            documentType = Constants.Template_FILE_TYPES.INSOLVENCY_ORDER;
            break;
          case "medicalReport":
            documentType = Constants.Template_FILE_TYPES.MEDICAL_REPORT;
            break;
          case "courtOrder":
            documentType = Constants.Template_FILE_TYPES.COURT_ORDER;
            break;
          case "statutoryDeclaration":
            documentType =
              Constants.Template_FILE_TYPES.DIRECTOR_STATUTORY_DECLARATION;
            break;
          default:
            return;
        }

        getDocumentData(documentType).then((fileName) => {
          if (fileName && mountedRef.current) {
            form.setFieldValue(fieldNameKey, fileName);
            updateDocumentName(field.name, fileName);
          }
        });
      }
    }, [
      form.values[field.name]?.subModeType,
      form.values[field.name]?.modeType,
      field.name,
      getDocumentData,
      form,
      updateDocumentName,
    ]);

    useEffect(() => {
      return () => {
        documentFetchedRef.current[field.name] = false;
        initializedRef.current[field.name] = false;
      };
    }, [field.name]);

    return (
      <div className="mt-2 flex flex-col">
        <DocumentSection {...componentProps} handleSetValue={handleSetValue} />
      </div>
    );
  }
);

DocumentsUpload.displayName = "DocumentsUpload";

const getDocumentTitle = (fieldName) => {
  switch (fieldName) {
    case "letterOfResignation":
      return "Letter of Resignation";
    case "statutoryDeclaration":
      return "Statutory Declaration";
    case "shareholdersSpecialNotice":
      return "Shareholder's Special Notice";
    case "deathCertificate":
      return "Death Certificate (Certified by Registrar of Deaths)";
    case "insolvencyOrder":
      return "Insolvency Order";
    case "medicalResignation":
      return "Medical Report";
    case "courtOrder":
      return "Court Order";
    case "noticeOfRemoval":
      return "Notice of Removal by Appointor";
    default:
      return fieldName;
  }
};

const DocumentSection = React.memo(
  ({
    form,
    documentName,
    handleSetValue,
    currentUser,
    company,
    sectionName,
    title,
    showRadioOptions,
    isStatutoryDeclaration,
    isLetterOfResignation,
    isNoticeOfRemoval,
    row,
    documentFetchedRef,
    updateDocumentName,
    isInUpdateCompanyDetailsContext = false,
  }) => {
    const radioOptionProps = useMemo(
      () => ({
        form,
        sectionName,
        handleSetValue,
      }),
      [form, sectionName, handleSetValue]
    );

    const documentData = useMemo(() => {
      return getChangedDocumentsModuleData(
        sectionName,
        "",
        form.values[sectionName]?.modeType,
        form.values[sectionName]?.subModeType
      );
    }, [
      sectionName,
      form.values[sectionName]?.modeType,
      form.values[sectionName]?.subModeType,
    ]);

    const acceptType = documentData[0]?.accept || "application/pdf";
    const fieldNameKey = `get${
      sectionName.charAt(0).toUpperCase() + sectionName.slice(1)
    }Name`;

    return (
      <div className="flex flex-col">
        <span className="block text-sm font-medium text-gray-700 uppercase">
          {title}:
        </span>
        {showRadioOptions && !isInUpdateCompanyDetailsContext && (
          <div className="grid grid-flow-col justify-stretch mt-4 mb-4">
            <RadioOption
              {...radioOptionProps}
              optionValue="pre-signed-documents"
              label="I have signed documents"
            />
            <RadioOption
              {...radioOptionProps}
              optionValue="not-signed-documents"
              label="Not yet signed"
            />
          </div>
        )}
        {(form.values[sectionName]?.modeType === "pre-signed-documents" ||
          isInUpdateCompanyDetailsContext) && (
          <FileUploadField
            sectionName={sectionName}
            accept={acceptType}
            fieldNameKey={fieldNameKey}
          />
        )}
        {(isStatutoryDeclaration ||
          isLetterOfResignation ||
          isNoticeOfRemoval) &&
          form.values[sectionName]?.modeType === "not-signed-documents" && (
            <PlatformResolutionSection
              documentName={documentName}
              form={form}
              currentUser={currentUser}
              company={company}
              sectionName={sectionName}
              row={row}
              documentFetchedRef={documentFetchedRef}
              updateDocumentName={updateDocumentName}
              acceptType={acceptType}
              fieldNameKey={fieldNameKey}
            />
          )}
      </div>
    );
  }
);

DocumentSection.displayName = "DocumentSection";

const RadioOption = React.memo(
  ({ form, sectionName, handleSetValue, optionValue, label }) => (
    <div className="flex items-center">
      <label
        htmlFor={`${sectionName}-${optionValue}`}
        className="flex items-center cursor-pointer"
        aria-label={label}
      >
        <Field
          id={`${sectionName}-${optionValue}`}
          name={`${sectionName}.modeType`}
          type="radio"
          value={optionValue}
          checked={form.values[sectionName]?.modeType === optionValue}
          onChange={handleSetValue}
          className="cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50"
        />
        <span className="ml-2">{label}</span>
      </label>
    </div>
  )
);

RadioOption.displayName = "RadioOption";

const FileUploadField = React.memo(({ sectionName, accept, fieldNameKey }) => (
  <div className="container bg-white mt-2 px-4 py-4 shadow-sm rounded-md">
    <Field
      name={sectionName}
      component={FileUploadForm}
      documentData={[
        {
          label: "Upload File",
          name: fieldNameKey,
          accept: accept,
        },
      ]}
      disabled={false}
      accept={accept}
    />
  </div>
));

FileUploadField.displayName = "FileUploadField";

const PlatformResolutionSection = React.memo(
  ({
    documentName,
    form,
    currentUser,
    company,
    sectionName,
    row,
    documentFetchedRef,
    updateDocumentName,
    acceptType,
    fieldNameKey,
  }) => {
    const dispatch = useDispatch();

    const handleSetValue = useCallback(
      async (e) => {
        const selectedValue = e.target.value;
        form.setFieldValue(`${sectionName}.subModeType`, selectedValue);

        if (
          selectedValue === "platform-resolution-type" &&
          form.values[sectionName]?.modeType === "not-signed-documents"
        ) {
          try {
            const response = await dispatch(
              getUploadChangeDocumentsAsync({
                companyId: company._id,
                data: {
                  officialId: form.values._id || row?.original?._id,
                  fileType:
                    sectionName === "statutoryDeclaration"
                      ? Constants.Template_FILE_TYPES
                          .DIRECTOR_STATUTORY_DECLARATION
                      : sectionName === "letterOfResignation"
                      ? Constants.Template_FILE_TYPES
                          .DIRECTOR_RESIGNATION_LETTER
                      : sectionName === "noticeOfRemoval"
                      ? Constants.Template_FILE_TYPES
                          .NOTICE_OF_REMOVAL_BY_APPOINTOR
                      : Constants.Template_FILE_TYPES
                          .DIRECTOR_RESIGNATION_LETTER,
                  dateOfCessation: form.values.cessationDate,
                  name: getFullName(form.values.names),
                  postalAddress: form.values.postalAddress,
                  cityOfResidence: form.values.residentialAddressDetails,
                  isNonResident: form.values.isNonResident,
                },
              })
            ).unwrap();

            if (response?.fileName) {
              form.setFieldValue(fieldNameKey, response.fileName);
              updateDocumentName(sectionName, response.fileName);
              documentFetchedRef.current[sectionName] = true;
            }
          } catch (error) {
            console.error("Failed to fetch document:", error);
          }
        }
      },
      [
        dispatch,
        company._id,
        form,
        sectionName,
        form.values.names,
        form.values.postalAddress,
        form.values.residentialAddressDetails,
        row?.original?._id,
        documentFetchedRef,
        updateDocumentName,
        fieldNameKey,
      ]
    );

    const sectionDocumentName = form.values[fieldNameKey];

    const documentEditorProps = useMemo(
      () => ({
        url: `https://sfl-bucket.s3.us-west-2.amazonaws.com/images/${sectionDocumentName}`,
        editorKey: `${currentUser._id}-${company._id}-${sectionName}-${sectionDocumentName}`,
        userFullName: getFullName(currentUser.names),
        userId: currentUser._id,
        companyId: company._id,
        documentType: "word",
        fileType: "docx",
        mode: "edit",
        title: `${format(new Date(), "dd/MM/yyyy")} - ${
          sectionName === "statutoryDeclaration"
            ? "Statutory Declaration"
            : sectionName === "noticeOfRemoval"
            ? "Notice of Removal by Appointor"
            : "Letter of Resignation"
        } - ${getFullName(currentUser.names)}`,
        containerHeight: "600px",
        permissions: {
          edit: true,
          comment: false,
          review: true,
        },
        metadata: {
          isFromChangesModule: "isFromChangesModule",
          fileName: sectionDocumentName,
        },
      }),
      [sectionDocumentName, currentUser, company._id, sectionName]
    );

    return (
      <div className="mt-2 bg-white px-4 py-2 shadow-sm rounded-md">
        <h3 className="mb-2">
          {sectionName === "statutoryDeclaration"
            ? "Statutory declaration approving the change:"
            : sectionName === "noticeOfRemoval"
            ? "Notice of Removal by Appointor:"
            : "Letter of Resignation:"}
        </h3>
        <div className="bg-[#EDF0F5] flex flex-col justify-evenly mb-4 px-4 py-4 shadow-sm rounded-md space-y-3">
          <div className="flex items-center">
            <label
              htmlFor={`${sectionName}-ownResolution`}
              className="flex items-center cursor-pointer"
              aria-label={`Upload your own ${
                sectionName === "statutoryDeclaration"
                  ? "Statutory Declaration"
                  : sectionName === "noticeOfRemoval"
                  ? "Notice of Removal by Appointor"
                  : "Letter of Resignation"
              }`}
            >
              <Field
                id={`${sectionName}-ownResolution`}
                name={`${sectionName}.subModeType`}
                type="radio"
                value="self-resolution-type"
                checked={
                  form.values[sectionName]?.subModeType ===
                  "self-resolution-type"
                }
                onChange={handleSetValue}
                className="cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50"
              />
              <span className="ml-2">
                Upload your own{" "}
                {sectionName === "statutoryDeclaration"
                  ? "Statutory Declaration"
                  : sectionName === "noticeOfRemoval"
                  ? "Notice of Removal by Appointor"
                  : "Letter of Resignation"}
              </span>
            </label>
          </div>
          {form.values[sectionName]?.subModeType === "self-resolution-type" && (
            <FileUploadField
              sectionName={sectionName}
              accept={acceptType}
              fieldNameKey={fieldNameKey}
            />
          )}
          <div className="flex items-center">
            <label
              htmlFor={`${sectionName}-platformResolution`}
              className="flex items-center cursor-pointer"
              aria-label={`Adopting our ${
                sectionName === "statutoryDeclaration"
                  ? "Statutory Declaration"
                  : sectionName === "noticeOfRemoval"
                  ? "Notice of Removal by Appointor"
                  : "Letter of Resignation"
              } Template`}
            >
              <Field
                id={`${sectionName}-platformResolution`}
                name={`${sectionName}.subModeType`}
                type="radio"
                value="platform-resolution-type"
                checked={
                  form.values[sectionName]?.subModeType ===
                  "platform-resolution-type"
                }
                onChange={handleSetValue}
                className="cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50"
              />
              <span className="ml-2">
                Adopting our{" "}
                {sectionName === "statutoryDeclaration"
                  ? "Statutory Declaration"
                  : sectionName === "noticeOfRemoval"
                  ? "Notice of Removal by Appointor"
                  : "Letter of Resignation"}
                {" Template"}
              </span>
            </label>
          </div>
          {form.values[sectionName]?.subModeType ===
            "platform-resolution-type" && (
            <div className="px-4 py-4 mt-2 mb-4">
              {sectionDocumentName ? (
                <OnlyOfficeDocumentEditor {...documentEditorProps} />
              ) : (
                <p className="flex justify-center py-4">
                  <SpinnerIcon className="text-gray-500" />
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

PlatformResolutionSection.displayName = "PlatformResolutionSection";

export default DocumentsUpload;

// New Accordion Wrapper Component
export const DocumentAccordion = ({
  reason,
  isSpecialRole,
  isAlternateDirector,
  row,
}) => {
  const [openKey, setOpenKey] = useState(null);

  const handleToggle = (key) => {
    setOpenKey((prevKey) => (prevKey === key ? null : key));
  };

  const renderAccordion = (key, title, fieldName) => (
    <div className="rounded-lg">
      <Disclosure defaultOpen={openKey === key}>
        {({ open }) => (
          <>
            <Disclosure.Button
              className="flex w-full justify-between px-4 py-3 text-left bg-gray-20 hover:bg-gray-50 rounded-lg"
              onClick={() => handleToggle(key)}
            >
              <span className="font-medium text-gray-900">{title}</span>
              <ChevronDownIcon
                className={`${
                  openKey === key ? "rotate-180 transform" : ""
                } h-5 w-5 text-gray-500`}
              />
            </Disclosure.Button>
            {openKey === key && (
              <Disclosure.Panel
                static
                className="px-4 py-3 border-t border-gray-100 bg-white rounded-b-lg"
              >
                <Field
                  name={fieldName}
                  component={DocumentsUpload}
                  idType={row.original.idType}
                  designation={row.original.designation}
                  row={row}
                />
              </Disclosure.Panel>
            )}
          </>
        )}
      </Disclosure>
    </div>
  );

  return (
    <div className="space-y-4">
      {reason === "resignation" && (
        <>
          {renderAccordion(
            "letterOfResignation",
            "Letter of Resignation",
            "letterOfResignation"
          )}
          {!isSpecialRole &&
            renderAccordion(
              "statutoryDeclaration",
              "Statutory Declaration",
              "statutoryDeclaration"
            )}
        </>
      )}

      {reason === "removal" && (
        <>
          {isAlternateDirector
            ? renderAccordion(
                "noticeOfRemoval",
                "Notice of Removal by Appointor",
                "noticeOfRemoval"
              )
            : !isSpecialRole &&
              renderAccordion(
                "shareholdersSpecialNotice",
                "Shareholder's Special Notice",
                "shareholdersSpecialNotice"
              )}
        </>
      )}

      {reason === "death" &&
        renderAccordion(
          "deathCertificate",
          "Death Certificate (Certified by Registrar of Deaths)",
          "deathCertificate"
        )}

      {reason === "bankruptcy" &&
        renderAccordion(
          "insolvencyOrder",
          "Insolvency Order",
          "insolvencyOrder"
        )}

      {reason === "insanity" &&
        renderAccordion("medicalReport", "Medical Report", "medicalReport")}

      {reason === "courtOrder" &&
        renderAccordion("courtOrder", "Court Order", "courtOrder")}
    </div>
  );
};
